import {
  StatefulTableColumnSnapshotSchema,
  type StatefulTableColumnSnapshot,
  type StatefulTableDefinitionResponse,
} from '@erp/rpc';
import { Num, T, useGT } from 'gt-react';
import type { ReactNode } from 'react';
import { useListStatefulTables } from 'src/api/rpc/hooks/stateful-tables/useStatefulTables';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import type { ConfigRendererProps } from './types';

interface StatefulTableReadConfigRendererProps extends ConfigRendererProps {
  icon: ReactNode;
  heading: ReactNode;
  selectLabel: ReactNode;
  versionLabel: ReactNode;
  keyLabel?: ReactNode;
  columnsHeading: ReactNode;
  help: ReactNode;
}

export function StatefulTableReadConfigRenderer({
  config,
  onConfigChange,
  readOnly,
  icon,
  heading,
  selectLabel,
  versionLabel,
  keyLabel,
  columnsHeading,
  help,
}: StatefulTableReadConfigRendererProps) {
  const state = useReadConfigState({ config, onConfigChange });
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b">
        {icon}
        <span className="text-sm font-medium">{heading}</span>
      </div>
      <StatefulTableSelect
        label={selectLabel}
        selectedId={state.selectedId}
        tables={state.tables}
        isLoading={state.isLoading}
        isError={state.isError}
        disabled={readOnly}
        onValueChange={state.selectTable}
      />
      <SelectedTableMetadata
        selected={state.selected}
        liveColumns={state.liveColumns}
        keyLabel={keyLabel}
        versionLabel={versionLabel}
        columnsHeading={columnsHeading}
      />
      <p className="text-xs text-muted-foreground">{help}</p>
    </div>
  );
}

function useReadConfigState({
  config,
  onConfigChange,
}: Pick<ConfigRendererProps, 'config' | 'onConfigChange'>) {
  const selectedId =
    typeof config.tableDefinitionId === 'string'
      ? config.tableDefinitionId
      : '';
  const parsedColumns = StatefulTableColumnSnapshotSchema.array().safeParse(
    config.columns,
  );
  const snapshottedColumns = parsedColumns.success ? parsedColumns.data : [];
  const {
    data: listData,
    isLoading,
    isError,
  } = useListStatefulTables({
    perPage: 100,
  });
  const tables = listData?.results ?? [];
  const selected = tables.find((table) => table.id === selectedId);
  const liveColumns: StatefulTableColumnSnapshot[] =
    selected?.currentVersion.columns.map(({ name, type }) => ({
      name,
      type,
    })) ?? [];

  return {
    selectedId,
    tables,
    selected,
    liveColumns,
    isLoading,
    isError,
    selectTable: (value: string) => {
      const chosen = tables.find((table) => table.id === value);
      onConfigChange({
        ...config,
        tableDefinitionId: value,
        columns:
          chosen?.currentVersion.columns.map(({ name, type }) => ({
            name,
            type,
          })) ?? snapshottedColumns,
      });
    },
  };
}

function SelectedTableMetadata({
  selected,
  liveColumns,
  keyLabel,
  versionLabel,
  columnsHeading,
}: {
  selected: StatefulTableDefinitionResponse | undefined;
  liveColumns: StatefulTableColumnSnapshot[];
  keyLabel?: ReactNode;
  versionLabel: ReactNode;
  columnsHeading: ReactNode;
}) {
  if (!selected) return null;
  return (
    <div className="rounded-md border p-3 space-y-2 text-sm">
      {keyLabel && (
        <MetadataRow
          label={keyLabel}
          value={selected.currentVersion.keyColumns.join(', ')}
        />
      )}
      <MetadataRow
        label={versionLabel}
        value={
          <T context="stateful table config version value">
            v<Num>{selected.currentVersion.versionNumber}</Num>
          </T>
        }
      />
      <div className="pt-1 border-t">
        <div className="text-muted-foreground text-xs mb-1">
          {columnsHeading}
        </div>
        <ul className="space-y-0.5">
          {liveColumns.map((column) => (
            <li
              key={column.name}
              className="flex justify-between font-mono text-xs"
            >
              <span>{column.name}</span>
              <span className="text-muted-foreground">{column.type}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function StatefulTableSelect({
  label,
  selectedId,
  tables,
  isLoading,
  isError,
  disabled = false,
  onValueChange,
}: {
  label: ReactNode;
  selectedId: string;
  tables: StatefulTableDefinitionResponse[];
  isLoading: boolean;
  isError: boolean;
  disabled?: boolean;
  onValueChange: (value: string) => void;
}) {
  const gt = useGT();
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <Select
        value={selectedId}
        onValueChange={onValueChange}
        disabled={isLoading || isError || disabled}
      >
        <SelectTrigger>
          <SelectValue
            placeholder={
              isLoading
                ? gt('Loading...', {
                    context: 'loading state for stateful table select',
                  })
                : gt('Choose a stateful table', {
                    context: 'placeholder for stateful table select',
                  })
            }
          />
        </SelectTrigger>
        <SelectContent>
          {tables.map((table) => (
            <SelectItem key={table.id} value={table.id}>
              {table.name}
            </SelectItem>
          ))}
          {tables.length === 0 && !isLoading && (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              {isError
                ? gt('Failed to load stateful tables.', {
                    context: 'error state for stateful table select',
                  })
                : gt(
                    'No stateful tables yet. Create one in the Stateful Tables tab.',
                    { context: 'empty state for stateful table select' },
                  )}
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

function MetadataRow({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-xs">{value}</span>
    </div>
  );
}
